import { useState } from 'react'
import { useBulkUpdateChecklist } from '../api/rooms'
import type {
  ChecklistBulkBody,
  ChecklistBulkField,
  ChecklistPriority,
  ChecklistSectionResponse,
} from '../api/types'
import { errorMessage } from '../lib/format'

/**
 * One edit for the ticked requests (ADR 0009 s9): a field at a time, all or nothing. FIELDS,
 * NEVER STATES: there is no bulk sign-off, because every move has a reason and an audience, and
 * no bulk delete, because deleting an item deletes its document links.
 *
 * "Set" with an empty value clears the field on every ticked request; that is why each field has
 * its own button instead of one form that would have to guess which blanks were meant.
 */
export function ChecklistBulkBar({
  roomId,
  selected,
  sections,
  onDone,
}: {
  roomId: string
  selected: string[]
  sections: ChecklistSectionResponse[]
  onDone: () => void
}) {
  const bulk = useBulkUpdateChecklist(roomId)
  const [priority, setPriority] = useState<ChecklistPriority | ''>('')
  const [goalDate, setGoalDate] = useState('')
  const [sectionId, setSectionId] = useState('')
  const [said, setSaid] = useState<string | null>(null)
  if (selected.length === 0) return null

  const apply = (field: ChecklistBulkField, body: Partial<ChecklistBulkBody>, what: string) => {
    setSaid(null)
    bulk.mutate(
      { itemIds: selected, fields: [field], ...body },
      {
        onSuccess: () => {
          setSaid(`${what} on ${selected.length} request${selected.length === 1 ? '' : 's'}.`)
          onDone()
        },
      },
    )
  }

  return (
    <section className="panel checklist-bulk" aria-label="Edit the ticked requests">
      <p className="small no-margin">
        <strong>{selected.length}</strong> ticked. One field at a time; either every ticked request
        changes or none does.
      </p>
      <div className="row-actions">
        <label className="field-inline">
          <span className="small">Priority</span>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as ChecklistPriority | '')}
          >
            <option value="">None</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </label>
        <button
          type="button"
          className="btn btn-sm"
          disabled={bulk.isPending}
          onClick={() => apply('priority', { priority: priority || null }, 'Priority set')}
        >
          Set priority
        </button>

        <label className="field-inline">
          <span className="small">Goal date</span>
          <input type="date" value={goalDate} onChange={(e) => setGoalDate(e.target.value)} />
        </label>
        <button
          type="button"
          className="btn btn-sm"
          disabled={bulk.isPending}
          onClick={() =>
            apply(
              'goalDate',
              { goalDate: goalDate || null },
              goalDate ? 'Goal date set' : 'Goal date cleared',
            )
          }
        >
          {goalDate ? 'Set goal date' : 'Clear goal date'}
        </button>

        <label className="field-inline">
          <span className="small">Move to</span>
          <select value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
            <option value="">No section</option>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} {s.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="btn btn-sm"
          disabled={bulk.isPending}
          onClick={() => apply('sectionId', { sectionId: sectionId || null }, 'Moved')}
        >
          Move
        </button>
      </div>
      <p className="small muted no-margin">Moved requests keep their labels.</p>
      {said && (
        <p className="small" role="status">
          {said}
        </p>
      )}
      {bulk.isError && <p className="error-text small">{errorMessage(bulk.error)}</p>}
    </section>
  )
}
